import { useEffect, useState } from '@wordpress/element';

/**
 * Custom hook to manage YouTube IFrame API and player initialization
 * @param {React.RefObject} playerRef          - Reference to store the player instance
 * @param {React.RefObject} shadowContainerRef - Reference to shadow DOM container
 * @param {boolean}         visible            - Whether the widget is visible
 * @param {string}          videoId            - YouTube video ID
 * @return {boolean} Whether the YouTube API is ready
 */
const useYouTubePlayer = (playerRef, shadowContainerRef, visible, videoId) => {
	const [playerReady, setPlayerReady] = useState(false);

	// Load YouTube IFrame API
	useEffect(() => {
		if (!window.YT) {
			const tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';
			const firstScriptTag = document.getElementsByTagName('script')[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

			window.onYouTubeIframeAPIReady = () => {
				setPlayerReady(true);
			};
		} else {
			setPlayerReady(true);
		}
	}, []);

	// Initialize YouTube player
	useEffect(() => {
		if (!playerReady || !visible || !shadowContainerRef.current) {
			return;
		}

		// Wait a bit for DOM to be ready
		setTimeout(() => {
			const wrapper =
				shadowContainerRef.current.querySelector('#pingvid-wrapper');
			if (!wrapper) {
				return;
			}

			const iframe = wrapper.querySelector('#pingvid-iframe');
			if (!iframe) {
				return;
			}

			playerRef.current = new window.YT.Player(iframe, {
				videoId,
				playerVars: {
					autoplay: 1,
					mute: 1,
					controls: 0,
					rel: 0,
					iv_load_policy: 3,
					fs: 0,
					enablejsapi: 1,
					playsinline: 1,
					origin: location.origin,
					loop: 1,
					playlist: videoId,
					vq: 'medium',
				},
				events: {
					onReady: (event) => {
						event.target.mute();
						event.target.seekTo(0);
						event.target.playVideo();
					},
					onError: (event) => {
						console.warn('YouTube Player error:', event.data);
					},
				},
			});
		}, 100);
	}, [playerReady, visible, videoId, shadowContainerRef, playerRef]);

	return playerReady;
};

export default useYouTubePlayer;
